import { useState, type ReactNode } from 'react';
import { ArrowDown, Check, Copy, ListPlus, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { copyText } from '@/lib/clipboard';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/spinner';
import { useNow } from '@/hooks/use-now';
import { formatElapsed, isLongMessage } from './agent-activity';

/**
 * SF-T-266: the pieces a conversation's messages share, in Chat and the task's Agent tab, laid out as t3code's
 * are: your messages in a bubble on the right that folds when long, the agent's as plain text with no label, and
 * each message's copy button and time under it, shown on hover (always on touch screens).
 */

const COPIED_MS = 1500;

/** A small copy button: the icon turns to a tick for a moment once it's copied. */
export function CopyTextButton({ text, label = 'Copy', className }: { text: string; label?: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!(await copyText(text))) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), COPIED_MS);
  };
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      className={cn('size-6 text-muted-foreground', className)}
      aria-label={copied ? 'Copied' : label}
      onClick={() => void copy()}
    >
      {copied ? <Check className="text-success-strong" /> : <Copy />}
    </Button>
  );
}

/**
 * A message's row of actions (copy, the time, a menu), under it. Hidden until the message is hovered or focused,
 * on a parent with `group/msg`; `shown` keeps it in view (the newest answer, a failed send).
 */
export function MessageActions({ children, shown = false, align = 'start', className }: { children: ReactNode; shown?: boolean; align?: 'start' | 'end'; className?: string }) {
  return (
    <div
      className={cn(
        'flex min-h-6 items-center gap-1 text-2xs text-muted-foreground tabular-nums',
        align === 'end' ? 'justify-end' : '-ml-1.5',
        !shown && 'opacity-0 transition-opacity duration-200 group-focus-within/msg:opacity-100 group-hover/msg:opacity-100 pointer-coarse:opacity-100',
        className
      )}
    >
      {children}
    </div>
  );
}

/**
 * Your message: a bubble on the right, folded to a few lines with a fade when it's long, and Show full message.
 * `cutShort` (the machine sent only the start): Show full message asks `onExpand` for the rest.
 */
export function UserMessageBubble({
  text,
  children,
  cutShort = false,
  onExpand,
  className,
}: {
  /** The text, to tell whether it's long. */
  text: string;
  /** The text as shown (Markdown, mentions). */
  children: ReactNode;
  cutShort?: boolean;
  /** Opened or closed (to read the rest of a message cut short). */
  onExpand?: (open: boolean) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const foldable = cutShort || isLongMessage(text);
  const folded = foldable && !open;
  const toggle = () => {
    setOpen(!open);
    onExpand?.(!open);
  };
  return (
    <div className={cn('max-w-[min(40rem,85%)] min-w-0 rounded-2xl bg-muted px-3.5 py-2', className)}>
      <div className={cn(folded && 'max-h-44 overflow-hidden [mask-image:linear-gradient(to_bottom,black_calc(100%-1.75rem),transparent)]')}>{children}</div>
      {foldable ? (
        <Button type="button" variant="link" size="xs" className="h-auto px-0 text-2xs" onClick={toggle}>
          {open ? 'Show less' : 'Show full message'}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * SF-T-266: on a phone, the turn going now as t3code's app shows it: a pill floating over the foot of the thread,
 * "Working 2m 51s", with how many subagents are at work and messages queued, and Jump to latest as a round button beside it when you've
 * scrolled up. Place it in a `relative` box round the scrolling one, as JumpToLatestButton.
 */
export function WorkingPill({
  since,
  agents,
  queued = 0,
  queuedPanel = null,
  offline = null,
  onJump,
  className,
}: {
  /** When the turn started (ISO), or null when it isn't known. */
  since: string | null;
  /** The thread's subagents: how many are working, of how many. */
  agents?: { running: number; total: number } | null;
  /** Messages waiting for the next turn. */
  queued?: number;
  /** SF-T-266: what "N queued" opens, as T3 Code's app's Queued panel (QueuedMessages' rows); without it, it's only a count. */
  queuedPanel?: ReactNode;
  /** SF-T-266: the machine isn't connected (its name): the pill says so instead of Working, as T3 Code's does. */
  offline?: string | null;
  /** Shown when the newest isn't in view. */
  onJump?: (() => void) | null;
  className?: string;
}) {
  const start = since ? Date.parse(since) : NaN;
  const now = useNow(1000, !Number.isNaN(start));
  return (
    <div className={cn('pointer-events-none absolute bottom-3 left-1/2 z-10 flex max-w-[calc(100%-1rem)] -translate-x-1/2 items-center gap-2', className)}>
      <div
        role="status"
        className="pointer-events-auto flex h-11 min-w-0 items-center gap-2 rounded-full border bg-background/90 px-3.5 text-sm whitespace-nowrap shadow-md backdrop-blur tabular-nums"
      >
        {offline ? (
          <>
            <span className="size-2 rounded-full bg-danger-strong" aria-hidden />
            <span>{offline} offline</span>
          </>
        ) : (
          <>
            <Spinner size="xs" />
            <span>{Number.isNaN(start) ? 'Working…' : `Working ${formatElapsed(Math.max(now, start) - start)}`}</span>
          </>
        )}
        {agents?.total ? (
          <>
            <span className="h-4 w-px bg-border" aria-hidden />
            <span className="flex items-center gap-1 text-muted-foreground" aria-label={`${agents.running} of ${agents.total} subagents working`}>
              <Users className="size-3.5" aria-hidden />
              {agents.running}/{agents.total}
            </span>
          </>
        ) : null}
        {queued ? (
          <>
            <span className="h-4 w-px bg-border" aria-hidden />
            {queuedPanel ? (
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="-my-2 flex items-center gap-1 rounded-full px-1 py-2 text-muted-foreground hover:text-foreground" aria-label={`${queued} queued: show them`}>
                    <ListPlus className="size-3.5" aria-hidden />
                    {queued} queued
                  </button>
                </PopoverTrigger>
                <PopoverContent side="top" align="center" collisionPadding={8} className="max-h-[50dvh] w-[min(28rem,calc(100vw-1rem))] overflow-y-auto">
                  <p className="text-center text-sm font-semibold">Queued</p>
                  {queuedPanel}
                </PopoverContent>
              </Popover>
            ) : (
              <span className="flex items-center gap-1 text-muted-foreground" aria-label={`${queued} queued`}>
                <ListPlus className="size-3.5" aria-hidden />
                {queued}
                <span className="max-sm:hidden"> queued</span>
              </span>
            )}
          </>
        ) : null}
      </div>
      {onJump ? (
        <Button type="button" variant="outline" size="icon" className="pointer-events-auto size-11 shrink-0 rounded-full shadow-md" aria-label="Jump to latest" onClick={onJump}>
          <ArrowDown />
        </Button>
      ) : null}
    </div>
  );
}

/** A picture sent with a message: its link, and a square crop of it when there is one. */
export interface MessagePicture {
  id: string;
  filename: string;
  url: string;
  square_url?: string | null;
}

/** Tiles shown in a collage at first; past that the last says "+N" and opens them all. */
const COLLAGE_TILES = 4;

/**
 * SF-T-266: the pictures sent with your message, above its bubble as t3code shows them: one at its own shape, several
 * as a collage of squares split by hairlines. Each opens in a new tab.
 */
export function MessagePictures({
  pictures,
  onOpen,
  className,
}: {
  pictures: readonly MessagePicture[];
  /** SF-T-266: a picture opens here (a dialog) rather than in a new tab: one read from a machine has no web address. */
  onOpen?: (picture: MessagePicture) => void;
  className?: string;
}) {
  const [all, setAll] = useState(false);
  if (!pictures.length) return null;
  // A link to the picture, or a button that opens it (onOpen).
  const opener = (p: MessagePicture, cls: string, children: ReactNode) =>
    onOpen ? (
      <button key={p.id} type="button" className={cls} data-hint={p.filename} aria-label={`Open ${p.filename}`} onClick={() => onOpen(p)}>
        {children}
      </button>
    ) : (
      <a key={p.id} href={p.url} target="_blank" rel="noreferrer" className={cls} data-hint={p.filename}>
        {children}
      </a>
    );
  if (pictures.length === 1) {
    const p = pictures[0]!;
    return opener(p, cn('block max-w-[min(16rem,85%)]', className), <img src={p.url} alt={p.filename} loading="lazy" className="max-h-48 rounded-2xl border object-contain" />);
  }
  // Past four, the fourth tile says "+N" and shows them all (each then opens on its own).
  const folded = !all && pictures.length > COLLAGE_TILES;
  const shown = folded ? pictures.slice(0, COLLAGE_TILES - 1) : pictures;
  const tile = 'relative block aspect-square bg-muted';
  return (
    <div className={cn('grid w-full max-w-80 grid-cols-2 gap-px overflow-hidden rounded-2xl border bg-border', className)}>
      {shown.map((p, i) =>
        opener(
          p,
          cn(tile, shown.length === 3 && !folded && i === 0 && 'row-span-2 aspect-auto'),
          <img src={p.square_url ?? p.url} alt={p.filename} loading="lazy" className="size-full object-cover" />
        )
      )}
      {folded ? (
        <button type="button" className={tile} aria-label={`Show all ${pictures.length} pictures`} onClick={() => setAll(true)}>
          <img src={pictures[COLLAGE_TILES - 1]!.square_url ?? pictures[COLLAGE_TILES - 1]!.url} alt="" loading="lazy" className="size-full object-cover" />
          <span className="absolute inset-0 flex items-center justify-center bg-foreground/50 text-lg font-medium text-background">
            +{pictures.length - (COLLAGE_TILES - 1)}
          </span>
        </button>
      ) : null}
    </div>
  );
}
